/**
 * filterStorage.js
 *
 * localStorage utilities for filter persistence.
 * All keys are namespaced under the "app:filters:" prefix to prevent
 * collisions with other localStorage entries.
 *
 * Usage:
 *   saveFilterState('graphs-overview', { filters: [...], page: 0 });
 *   const state = loadFilterState('graphs-overview'); // or null
 *   clearFilterState('graphs-overview');
 */

const LS_PREFIX = 'app:filters:';

/**
 * Saves filter state to localStorage under a namespaced key.
 * Silently ignores failures (private mode, storage quota exceeded, etc.).
 *
 * @param {string} pageKey - Unique page identifier (e.g. 'graphs-overview')
 * @param {Object} state   - The state object to persist
 */
export function saveFilterState(pageKey, state) {
    try {
        localStorage.setItem(LS_PREFIX + pageKey, JSON.stringify(state));
    } catch (e) {
        // localStorage unavailable or quota exceeded - gracefully ignore
    }
}

/**
 * Loads filter state from localStorage.
 * Returns null on any error (missing key, malformed JSON, unexpected shape).
 *
 * @param  {string}      pageKey - Unique page identifier
 * @returns {Object|null}         The persisted state, or null if unavailable/invalid
 */
export function loadFilterState(pageKey) {
    try {
        const raw = localStorage.getItem(LS_PREFIX + pageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // Guard against corrupted/stale values that aren't objects
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
        return parsed;
    } catch (e) {
        // Malformed JSON or other parse error - treat as if nothing is stored
        return null;
    }
}

/**
 * Removes filter state from localStorage for the given page key.
 * Silently ignores failures.
 *
 * @param {string} pageKey - Unique page identifier
 */
export function clearFilterState(pageKey) {
    try {
        localStorage.removeItem(LS_PREFIX + pageKey);
    } catch (e) {
        // Silently ignore
    }
}
